import { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import StatusBadge from '../components/StatusBadge';
import { STATUS_LABELS, STATUS_TRANSITIONS, STATUS_COLORS } from '../utils/statuses';

const COLORS = ['#3b82f6','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#94a3b8','#f97316'];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('parts');
  const [parts, setParts] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [filters, setFilters] = useState({ status: '', city: '', colleague_id: '', search: '' });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({});

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.city) params.city = filters.city;
      if (filters.colleague_id) params.colleague_id = filters.colleague_id;
      if (filters.search) params.search = filters.search;
      const res = await api.get('/api/parts', { params });
      setParts(res.data);
    } finally { setLoading(false); }
  }, [filters]);

  const fetchColleagues = async () => { const res = await api.get('/api/users'); setColleagues(res.data); };
  const fetchSummary = async () => { const res = await api.get('/api/reports/summary'); setSummary(res.data); };
  const fetchSettings = async () => { const res = await api.get('/api/settings'); setSettings(res.data); };

  useEffect(() => { fetchParts(); }, [fetchParts]);
  useEffect(() => { fetchColleagues(); }, []);
  useEffect(() => { if (tab === 'reports') fetchSummary(); }, [tab]);
  useEffect(() => { if (tab === 'settings') fetchSettings(); }, [tab]);

  const cities = [...new Set(colleagues.map(c => c.city).filter(Boolean))];

  const exportExcel = async () => {
    try {
      const res = await api.get('/api/parts/export/excel');
      const data = res.data.map(p => ({
        'شناسه': p.id, 'همکار': p.colleague_name, 'شهر': p.city,
        'نام قطعه': p.part_name, 'وضعیت': STATUS_LABELS[p.status] || p.status,
        'هزینه (تومان)': p.repair_cost || 0, 'پرداخت': p.payment_status === 'paid' ? 'پرداخت شده' : 'پرداخت نشده',
        'زمانبر (روز)': p.duration_days || '', 'تاریخ ثبت': p.created_date,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'قطعات');
      XLSX.writeFile(wb, 'گزارش-قطعات.xlsx');
    } catch (err) { alert('خطا در دریافت داده'); }
  };

  const s = {
    page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'Vazirmatn,Tahoma,sans-serif', direction: 'rtl' },
    header: { background: 'linear-gradient(135deg,#1a1a2e,#0f3460)', color: '#fff', padding: '0 1.5rem', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    tabs: { background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', padding: '0 1rem', overflowX: 'auto' },
    tab: (active) => ({ padding: '14px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem', color: active ? '#0f3460' : '#64748b', borderBottom: active ? '3px solid #0f3460' : '3px solid transparent', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }),
    main: { padding: '1.5rem', maxWidth: 960, margin: '0 auto' },
    card: { background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,.07)', border: '1px solid #f1f5f9', marginBottom: 10 },
    input: { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontFamily: 'inherit', fontSize: '0.95rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
    label: { display: 'block', fontSize: '0.88rem', fontWeight: 600, color: '#374151', marginBottom: 5, marginTop: 12 },
    btn: (color = '#0f3460') => ({ background: color, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.9rem' }),
    error: { color: '#dc2626', fontSize: '0.85rem', marginTop: 6 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
    modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto', direction: 'rtl', fontFamily: 'Vazirmatn,Tahoma,sans-serif' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 1.5rem', borderBottom: '1px solid #f1f5f9' },
    modalBody: { padding: '1.2rem 1.5rem' },
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>🔧 یاراسمارت — مدیریت</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.9rem', opacity: 0.85 }}>{user?.name}</span>
          <button onClick={logout} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>خروج</button>
        </div>
      </header>

      <nav style={s.tabs}>
        {[['parts','قطعات'],['colleagues','همکاران'],['reports','گزارش'],['settings','تنظیمات']].map(([id,label]) => (
          <button key={id} style={s.tab(tab===id)} onClick={() => setTab(id)}>{label}</button>
        ))}
      </nav>

      <main style={s.main}>
        {tab === 'parts' && <PartsTab parts={parts} colleagues={colleagues} cities={cities} filters={filters} setFilters={setFilters} loading={loading} onRefresh={fetchParts} exportExcel={exportExcel} s={s} />}
        {tab === 'colleagues' && <ColleaguesTab colleagues={colleagues} onRefresh={fetchColleagues} s={s} />}
        {tab === 'reports' && <ReportsTab summary={summary} s={s} />}
        {tab === 'settings' && <SettingsTab settings={settings} onSave={fetchSettings} s={s} />}
      </main>
    </div>
  );
}

function PartsTab({ parts, colleagues, cities, filters, setFilters, loading, onRefresh, exportExcel, s }) {
  const [addModal, setAddModal] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const deletePart = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try { await api.delete(`/api/parts/${id}`); onRefresh(); } catch { alert('خطا در حذف'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={s.btn()} onClick={() => setAddModal(true)}>+ ثبت قطعه</button>
        <button onClick={exportExcel} style={s.btn('#059669')}>📥 Excel</button>
        <input style={{ ...s.input, flex: 1, minWidth: 180 }} placeholder="جستجو..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.88rem', background: '#fff' }} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.88rem', background: '#fff' }} value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}>
          <option value="">همه شهرها</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.88rem', background: '#fff' }} value={filters.colleague_id} onChange={e => setFilters(f => ({ ...f, colleague_id: e.target.value }))}>
          <option value="">همه همکاران</option>
          {colleagues.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(filters.status || filters.city || filters.colleague_id || filters.search) && (
          <button onClick={() => setFilters({ status: '', city: '', colleague_id: '', search: '' })} style={{ color: '#dc2626', background: 'none', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>پاک کردن</button>
        )}
      </div>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 10 }}>{parts.length} قطعه</p>
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>بارگذاری...</div> : (
        <div>
          {parts.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>قطعه‌ای یافت نشد</div>}
          {parts.map(part => (
            <div key={part.id} style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <span style={{ fontWeight: 700, color: '#1e293b' }}>{part.part_name}</span>
                  <span style={{ color: '#475569', fontSize: '0.9rem' }}> — {part.colleague_name}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}> ({part.colleague_city || '—'})</span>
                </div>
                <StatusBadge status={part.status} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{new Date(part.created_at).toLocaleDateString('fa-IR')}</span>
                {part.duration_days && <span style={{ color: '#6366f1', fontSize: '0.8rem' }}>⏱ {part.duration_days} روز</span>}
                {part.repair_cost > 0 && <span style={{ color: '#0f3460', fontSize: '0.85rem', fontWeight: 600 }}>{Number(part.repair_cost).toLocaleString('fa-IR')} تومان</span>}
                <div style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
                  <button onClick={() => setDetailModal(part)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', color: '#475569' }}>جزئیات</button>
                  {STATUS_TRANSITIONS[part.status]?.length > 0 && (
                    <button onClick={() => setStatusModal(part)} style={{ background: '#0f3460', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem' }}>تغییر وضعیت</button>
                  )}
                  <button onClick={() => deletePart(part.id)} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem' }}>حذف</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {addModal && <AddPartModal colleagues={colleagues} onClose={() => setAddModal(false)} onSave={() => { setAddModal(false); onRefresh(); }} s={s} />}
      {statusModal && <ChangeStatusModal part={statusModal} onClose={() => setStatusModal(null)} onSave={() => { setStatusModal(null); onRefresh(); }} s={s} />}
      {detailModal && <DetailModal partId={detailModal.id} onClose={() => setDetailModal(null)} onRefresh={onRefresh} s={s} />}
    </div>
  );
}

function AddPartModal({ colleagues, onClose, onSave, s }) {
  const [form, setForm] = useState({ colleague_id: '', part_name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!form.colleague_id || !form.part_name) return setError('همکار و نام قطعه الزامی است');
    setLoading(true);
    try { await api.post('/api/parts', form); onSave(); }
    catch (err) { setError(err.response?.data?.error || 'خطا'); }
    finally { setLoading(false); }
  };
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>ثبت قطعه جدید</h2><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button></div>
        <div style={s.modalBody}>
          <label style={s.label}>همکار</label>
          <select style={s.input} value={form.colleague_id} onChange={e => setForm(f => ({ ...f, colleague_id: e.target.value }))}>
            <option value="">انتخاب همکار...</option>
            {colleagues.map(c => <option key={c.id} value={c.id}>{c.name} — {c.city || '—'}</option>)}
          </select>
          <label style={s.label}>نام قطعه</label>
          <input style={s.input} value={form.part_name} onChange={e => setForm(f => ({ ...f, part_name: e.target.value }))} placeholder="مثلاً: برد اصلی سامسونگ A52" />
          <label style={s.label}>توضیحات (اختیاری)</label>
          <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          {error && <p style={s.error}>{error}</p>}
          <button style={{ ...s.btn(), marginTop: 12, width: '100%' }} onClick={submit} disabled={loading}>{loading ? 'در حال ثبت...' : 'ثبت قطعه'}</button>
        </div>
      </div>
    </div>
  );
}

function ChangeStatusModal({ part, onClose, onSave, s }) {
  const nextStatuses = STATUS_TRANSITIONS[part.status] || [];
  const [status, setStatus] = useState(nextStatuses[0] || '');
  const [repairCost, setRepairCost] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setLoading(true);
    try { await api.patch(`/api/parts/${part.id}/status`, { status, repair_cost: repairCost ? parseInt(repairCost) : undefined, note: note || undefined }); onSave(); }
    catch (err) { setError(err.response?.data?.error || 'خطا'); }
    finally { setLoading(false); }
  };
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>تغییر وضعیت — {part.part_name}</h2><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button></div>
        <div style={s.modalBody}>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 12 }}>وضعیت فعلی: <StatusBadge status={part.status} /></p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {nextStatuses.map(st => {
              const col = STATUS_COLORS[st];
              return (
                <button key={st} onClick={() => setStatus(st)} style={{ padding: '8px 14px', borderRadius: 20, border: `2px solid ${status === st ? col.text : col.border}`, background: status === st ? col.bg : '#fff', color: col.text, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.88rem' }}>
                  {STATUS_LABELS[st]}
                </button>
              );
            })}
          </div>
          {status === 'repaired' && (
            <>
              <label style={s.label}>هزینه تعمیر (تومان)</label>
              <input style={s.input} type="number" placeholder="0" value={repairCost} onChange={e => setRepairCost(e.target.value)} dir="ltr" />
            </>
          )}
          <label style={s.label}>یادداشت (اختیاری)</label>
          <textarea style={{ ...s.input, minHeight: 60 }} value={note} onChange={e => setNote(e.target.value)} />
          {error && <p style={s.error}>{error}</p>}
          <button style={{ ...s.btn(), marginTop: 12, width: '100%' }} onClick={submit} disabled={loading || !status}>{loading ? 'در حال ثبت...' : 'ثبت تغییر'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ partId, onClose, onRefresh, s }) {
  const [part, setPart] = useState(null);
  useEffect(() => { api.get(`/api/parts/${partId}`).then(r => setPart(r.data)); }, [partId]);
  const markPaid = async () => { await api.patch(`/api/parts/${partId}/payment`, { payment_status: 'paid' }); api.get(`/api/parts/${partId}`).then(r => setPart(r.data)); onRefresh(); };
  if (!part) return (
    <div style={s.overlay}><div style={s.modal}><div style={s.modalBody}><div style={{ textAlign: 'center', padding: 40 }}>بارگذاری...</div></div></div></div>
  );
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{part.part_name}</h2><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button></div>
        <div style={s.modalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', alignItems: 'center' }}>
            <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>همکار</span><span>{part.colleague_name} — {part.colleague_city || '—'}</span>
            <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>وضعیت</span><StatusBadge status={part.status} />
            <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>تاریخ ثبت</span><span>{new Date(part.created_at).toLocaleDateString('fa-IR')}</span>
            {part.duration_days && <><span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>زمانبر</span><span style={{ color: '#6366f1', fontWeight: 600 }}>{part.duration_days} روز</span></>}
            {part.repair_cost > 0 && (
              <><span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>هزینه</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {Number(part.repair_cost).toLocaleString('fa-IR')} تومان
                {part.payment_status === 'unpaid' ? <button onClick={markPaid} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>ثبت پرداخت</button> : <span style={{ color: '#059669', fontSize: '0.8rem' }}>✓ پرداخت شده</span>}
              </span></>
            )}
          </div>
          {part.history?.length > 0 && (
            <>
              <p style={{ fontWeight: 700, marginTop: 16, marginBottom: 8 }}>تاریخچه</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {part.history.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#f8fafc', borderRadius: 8 }}>
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{new Date(h.created_at).toLocaleDateString('fa-IR')}</span>
                    <StatusBadge status={h.new_status} />
                    {h.note && <span style={{ fontSize: '0.8rem', color: '#475569' }}>{h.note}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ColleaguesTab({ colleagues, onRefresh, s }) {
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const deleteColleague = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try { await api.delete(`/api/users/${id}`); onRefresh(); } catch { alert('خطا در حذف'); }
  };
  return (
    <div>
      <div style={{ marginBottom: 14 }}><button style={s.btn()} onClick={() => setAddModal(true)}>+ افزودن همکار</button></div>
      <div>
        {colleagues.map(c => (
          <div key={c.id} style={{ ...s.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span style={{ fontWeight: 700, color: '#1e293b' }}>{c.name}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}> — {c.city || 'شهر ثبت نشده'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#64748b', fontSize: '0.9rem' }} dir="ltr">{c.phone}</span>
              {Number(c.total_debt) > 0 && <span style={{ background: '#FEF2F2', color: '#dc2626', border: '1px solid #FECACA', borderRadius: 20, padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600 }}>{Number(c.total_debt).toLocaleString('fa-IR')} ت بدهی</span>}
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{c.total_parts || 0} قطعه</span>
              <button onClick={() => setEditModal(c)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', color: '#475569' }}>ویرایش</button>
              <button onClick={() => deleteColleague(c.id)} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem' }}>حذف</button>
            </div>
          </div>
        ))}
        {colleagues.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>همکاری ثبت نشده</div>}
      </div>
      {addModal && <ColleagueModal onClose={() => setAddModal(false)} onSave={() => { setAddModal(false); onRefresh(); }} s={s} />}
      {editModal && <ColleagueModal colleague={editModal} onClose={() => setEditModal(null)} onSave={() => { setEditModal(null); onRefresh(); }} s={s} />}
    </div>
  );
}

function ColleagueModal({ colleague, onClose, onSave, s }) {
  const [form, setForm] = useState({ name: colleague?.name || '', phone: colleague?.phone || '', city: colleague?.city || '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!form.name || !form.phone) return setError('نام و شماره الزامی است');
    setLoading(true);
    try {
      const data = { name: form.name, phone: form.phone, city: form.city };
      if (form.password) data.password = form.password;
      if (colleague) await api.put(`/api/users/${colleague.id}`, data);
      else await api.post('/api/users', data);
      onSave();
    } catch (err) { setError(err.response?.data?.error || 'خطا'); }
    finally { setLoading(false); }
  };
  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.modalHeader}><h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{colleague ? 'ویرایش همکار' : 'افزودن همکار'}</h2><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button></div>
        <div style={s.modalBody}>
          <label style={s.label}>نام</label><input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <label style={s.label}>شماره موبایل</label><input style={s.input} type="tel" dir="ltr" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <label style={s.label}>شهر</label><input style={s.input} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          <label style={s.label}>رمز عبور {colleague ? '(خالی = بدون تغییر)' : '(اختیاری)'}</label>
          <input style={s.input} type="password" placeholder={colleague ? 'برای تغییر رمز وارد کنید' : 'رمز عبور'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          {error && <p style={s.error}>{error}</p>}
          <button style={{ ...s.btn(), marginTop: 12, width: '100%' }} onClick={submit} disabled={loading}>{loading ? '...' : 'ذخیره'}</button>
        </div>
      </div>
    </div>
  );
}

function ReportsTab({ summary, s }) {
  if (!summary) return <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>بارگذاری...</div>;
  const { totals, by_status, by_colleague } = summary;

  const pieData = by_status.map(r => ({ name: STATUS_LABELS[r.status] || r.status, value: parseInt(r.count) }));
  const barData = by_colleague.slice(0, 10).map(c => ({ name: c.name, قطعات: parseInt(c.total), بدهی: parseInt(c.debt || 0) }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 24 }}>
        {[['کل قطعات', totals.total_parts, '#0f3460'], ['قطعات فعال', totals.active_parts, '#f59e0b'], ['کل هزینه', `${Number(totals.total_repair_cost||0).toLocaleString('fa-IR')} ت`, '#059669'], ['کل بدهی', `${Number(totals.total_debt||0).toLocaleString('fa-IR')} ت`, '#dc2626'], ['میانگین زمان', `${totals.avg_duration||0} روز`, '#6366f1']].map(([l,v,c]) => (
          <div key={l} style={{ background: '#fff', borderRadius: 12, padding: '16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.07)', borderTop: `3px solid ${c}` }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: c }}>{v}</div>
            <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 12, color: '#0d2137' }}>وضعیت قطعات</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({name,value})=>`${value}`}>
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.07)' }}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 12, color: '#0d2137' }}>عملکرد همکاران</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="قطعات" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.07)', overflowX: 'auto' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 12, color: '#0d2137' }}>جزئیات همکاران</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr>{['همکار','شهر','کل','هزینه کل','بدهی'].map(h => <th key={h} style={{ background: '#f8fafc', padding: '8px 12px', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {by_colleague.map(c => (
              <tr key={c.name}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>{c.name}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>{c.city||'—'}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>{c.total}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>{Number(c.total_cost||0).toLocaleString('fa-IR')}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: Number(c.debt)>0?'#dc2626':'#059669', fontWeight: 600 }}>{Number(c.debt||0).toLocaleString('fa-IR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab({ settings, onSave, s }) {
  const [form, setForm] = useState({ sms_on_status_change: 'true', sms_sender: '', sms_api_key: '', sms_otp_template_id: '', sms_status_template_id: '' });
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (settings) setForm(f => ({ ...f, ...settings })); }, [settings]);
  const save = async () => {
    await api.put('/api/settings', form);
    setSaved(true); setTimeout(() => setSaved(false), 2000); onSave();
  };
  return (
    <div style={{ maxWidth: 500 }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0d2137', marginBottom: 12 }}>تنظیمات SMS.ir</h3>
      <label style={s.label}>API Key</label>
      <input style={s.input} type="password" dir="ltr" placeholder="برای تغییر وارد کنید" value={form.sms_api_key || ''} onChange={e => setForm(f => ({ ...f, sms_api_key: e.target.value }))} />
      <label style={s.label}>شماره فرستنده</label>
      <input style={s.input} dir="ltr" placeholder="30002128012468" value={form.sms_sender || ''} onChange={e => setForm(f => ({ ...f, sms_sender: e.target.value }))} />
      <label style={s.label}>Template ID — کد تایید (OTP)</label>
      <input style={s.input} dir="ltr" type="number" placeholder="مثلاً: 538706" value={form.sms_otp_template_id || ''} onChange={e => setForm(f => ({ ...f, sms_otp_template_id: e.target.value }))} />
      <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4, marginBottom: 8 }}>در SMS.ir قالب تایید با پارامتر Code بساز</p>
      <label style={s.label}>Template ID — اطلاع‌رسانی وضعیت</label>
      <input style={s.input} dir="ltr" type="number" placeholder="مثلاً: 802440" value={form.sms_status_template_id || ''} onChange={e => setForm(f => ({ ...f, sms_status_template_id: e.target.value }))} />
      <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4, marginBottom: 8 }}>در SMS.ir قالب با پارامترهای Part و Status بساز</p>
      <label style={s.label}>ارسال پیامک هنگام تغییر وضعیت</label>
      <select style={s.input} value={form.sms_on_status_change || 'true'} onChange={e => setForm(f => ({ ...f, sms_on_status_change: e.target.value }))}>
        <option value="true">فعال</option>
        <option value="false">غیرفعال</option>
      </select>
      <button style={{ ...s.btn(saved ? '#059669' : '#0f3460'), marginTop: 16, width: '100%' }} onClick={save}>{saved ? '✓ ذخیره شد' : 'ذخیره تنظیمات'}</button>
    </div>
  );
}
